import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const SLIDES = [
  { visual: '🗺️', pulse: true, titleKey: 'onboarding.slide1_title', bodyKey: 'onboarding.slide1_body' },
  { visual: '📍', pulse: false, titleKey: 'onboarding.slide2_title', bodyKey: 'onboarding.slide2_body' },
  { visual: '🎖️', pulse: false, titleKey: 'onboarding.slide3_title', bodyKey: 'onboarding.slide3_body' },
] as const;

export function OnboardingRoute() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [i, setI] = useState(0);
  const slide = SLIDES[i]!;
  const isLast = i === SLIDES.length - 1;

  const onNext = () => {
    if (isLast) {
      // Onboarding finished. Mark it so we don't show it again, then push
      // the user toward sign-in (skippable from there).
      try {
        localStorage.setItem('opensoi.onboarded', '1');
      } catch {
        /* private-mode storage; ignore */
      }
      nav('/signin');
      return;
    }
    setI(i + 1);
  };

  return (
    <div className="mx-auto flex min-h-dvh max-w-[480px] flex-col px-4 py-6">
      <div className="mb-3 flex items-center justify-end">
        <Link to="/" className="text-sm text-ink-soft hover:underline">
          {t('onboarding.skip')}
        </Link>
      </div>

      <section className="flex flex-1 flex-col items-center justify-center text-center">
        <div
          className="mb-6 grid h-56 w-56 place-items-center rounded-full text-7xl shadow-s2"
          style={{ background: 'linear-gradient(135deg, var(--soi-100), var(--soi-400))' }}
          aria-hidden="true"
        >
          {slide.pulse ? (
            <div style={{ position: 'relative', width: 36, height: 36 }}>
              <div className="pin open" />
              <div className="pin-pulse" />
            </div>
          ) : (
            slide.visual
          )}
        </div>
        <h2 className="m-0 mb-3 text-3xl font-bold leading-tight">{t(slide.titleKey)}</h2>
        <p className="m-0 max-w-[320px] text-base leading-snug text-ink-soft">{t(slide.bodyKey)}</p>
      </section>

      <div className="flex justify-center gap-2 py-4" aria-hidden="true">
        {SLIDES.map((_, idx) => (
          <span
            key={idx}
            className="h-2 rounded transition-all"
            style={{
              width: idx === i ? 24 : 8,
              background: idx === i ? 'var(--soi-600)' : 'var(--border)',
            }}
          />
        ))}
      </div>

      <div className="flex items-center gap-3 pb-[env(safe-area-inset-bottom,0)]">
        <Link
          to="/"
          className="inline-flex min-h-[44px] flex-1 items-center justify-center rounded-md px-4 py-2 text-sm font-semibold text-ink-soft hover:bg-soi-100"
        >
          {t('onboarding.skip')}
        </Link>
        <button
          type="button"
          onClick={onNext}
          className="inline-flex min-h-[44px] flex-[2] items-center justify-center rounded-md bg-soi-600 px-4 py-2 text-sm font-semibold text-white hover:bg-soi-700"
        >
          {isLast ? t('onboarding.finish') : t('onboarding.next')}
        </button>
      </div>
    </div>
  );
}
